import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuth } from './useAuth';
import api from '../utils/api';

// Polling Intervall: 10 Sekunden
const POLL_MS = 10_000;

// localStorage Key für persistenten Push-Status
const LS_KEY_PUSH = 'fg_push_enabled';
const LS_KEY_SUB  = 'fg_push_subscribed';

// VAPID Key (muss mit Backend übereinstimmen)
const VAPID_PUBLIC_KEY = 'BE5rnmPpcGBoerT7X3DYXE-qxEHVypsqVLTf9dRZkmtGGrQCod8p_iQwk_CY0ZVu_IYNvOjY-Je9o1pdEu01r28';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64  = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const output  = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    output[i] = rawData.charCodeAt(i);
  }
  return output;
}

// Prüfen ob als PWA installiert
function isInstalled() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

// Service Worker registrieren
async function registerSW() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register('/service-worker.js', {
      scope: '/',
      updateViaCache: 'none',
    });
    await navigator.serviceWorker.ready;
    return reg;
  } catch(e) {
    console.warn('SW Registration failed:', e);
    return null;
  }
}

// Notification anzeigen (über Service Worker für iOS-Kompatibilität)
async function showLocalNotification(title, body, tag) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  try {
    // Immer über Service Worker — das ist der einzige Weg der auf iOS PWA funktioniert
    const swReg = await navigator.serviceWorker?.ready.catch(() => null);
    if (swReg?.showNotification) {
      await swReg.showNotification(title || 'FuhrparkGerhards', {
        body:    body || '',
        icon:    '/icon-192.png',
        badge:   '/badge-72.png',
        tag:     tag || `fg-${Date.now()}`,
        silent:  false,
      });
    } else {
      // Fallback für Desktop-Browser
      new Notification(title || 'FuhrparkGerhards', {
        body:  body || '',
        icon:  '/icon-192.png',
        tag:   tag || `fg-${Date.now()}`,
      });
    }
  } catch(e) {
    console.warn('showLocalNotification error:', e);
  }
}

export function usePushNotifications() {
  const { user } = useAuth();
  const timerRef  = useRef(null);
  const shownRef  = useRef(new Set());
  const regRef    = useRef(null);

  // Initialer State aus localStorage (bleibt über Seitenwechsel erhalten)
  const [permission, setPermission] = useState(() => {
    if (!('Notification' in window)) return 'unsupported';
    return Notification.permission;
  });

  const [subscribed, setSubscribed] = useState(() => {
    // Aus localStorage lesen damit Toggle nach Seitenwechsel korrekt bleibt
    try { return localStorage.getItem(LS_KEY_SUB) === '1'; }
    catch { return false; }
  });

  const [installed, setInstalled] = useState(isInstalled);

  // subscribed in localStorage speichern wenn sich Wert ändert
  const setSubscribedPersist = useCallback((val) => {
    setSubscribed(val);
    try { localStorage.setItem(LS_KEY_SUB, val ? '1' : '0'); }
    catch {}
  }, []);

  // SW registrieren beim Start
  useEffect(() => {
    registerSW().then(reg => { regRef.current = reg; });

    // PWA-Modus beobachten
    const mq = window.matchMedia('(display-mode: standalone)');
    const handler = (e) => setInstalled(e.matches);
    mq.addEventListener('change', handler);

    return () => mq.removeEventListener('change', handler);
  }, []);

  // Beim Login: echten Status vom Backend laden und mit localStorage abgleichen
  useEffect(() => {
    if (!user) return;

    // Aktuelle Browser-Permission setzen
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }

    // Backend-Status laden
    api.get('/notifications/settings')
      .then(r => {
        const backendEnabled    = !!r.data.push_enabled;
        const backendSubscribed = !!r.data.subscribed;

        // Wenn Backend sagt subscribed=true, localStorage aktualisieren
        if (backendEnabled && backendSubscribed) {
          setSubscribedPersist(true);
        } else if (!backendEnabled) {
          // Backend sagt deaktiviert — localStorage auch deaktivieren
          setSubscribedPersist(false);
        }
        // Wenn backendEnabled=true aber subscribed=false:
        // localStorage-Wert beibehalten (könnte frischer sein)
      })
      .catch(() => {
        // Bei Fehler localStorage-Wert beibehalten
      });
  }, [user, setSubscribedPersist]);

  // Polling: ungelesene Notifications vom Server holen
  const poll = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get('/notifications/poll');
      if (!Array.isArray(data) || data.length === 0) return;

      for (const n of data) {
        if (shownRef.current.has(n.id)) continue;
        shownRef.current.add(n.id);
        // Notification anzeigen (auch wenn App offen ist)
        await showLocalNotification(n.title, n.body, `fg-${n.id}`);
      }
    } catch {
      // Silent fail
    }
  }, [user]);

  // Polling starten wenn eingeloggt
  useEffect(() => {
    if (!user) return;
    // Sofort pollen
    poll();
    // Dann alle 10 Sekunden
    timerRef.current = setInterval(poll, POLL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [user, poll]);

  // Sofort pollen wenn App sichtbar wird (z.B. nach Entsperren des Handys)
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') poll();
    };
    const onFocus = () => poll();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [poll]);

  // Push aktivieren: Berechtigung anfragen + Web Push Subscription erstellen
  const enable = useCallback(async () => {
    if (!('Notification' in window)) {
      return { ok: false, reason: 'unsupported' };
    }

    // 1. Berechtigung anfragen
    let perm = Notification.permission;
    if (perm === 'default') {
      perm = await Notification.requestPermission();
    }
    setPermission(perm);

    if (perm !== 'granted') {
      return { ok: false, reason: 'denied' };
    }

    // 2. Service Worker registrieren
    const reg = regRef.current || await registerSW();
    if (!reg) {
      // Kein SW aber Berechtigung — Polling-only Modus
      await api.put('/notifications/settings', { push_enabled: true });
      setSubscribedPersist(true);
      return { ok: true, mode: 'polling' };
    }

    // 3. Web Push Subscription erstellen (für echten Hintergrund-Push)
    if (reg.pushManager) {
      try {
        // Prüfen ob schon subscribed
        let sub = await reg.pushManager.getSubscription();
        if (!sub) {
          // Neue Subscription erstellen
          sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
          });
        }

        // Subscription ans Backend schicken
        const subJson = sub.toJSON();
        if (!subJson.keys?.p256dh || !subJson.keys?.auth) {
          throw new Error('Subscription hat keine Keys');
        }

        const response = await api.post('/notifications/subscribe', {
          subscription: subJson,
        });

        if (response.data?.ok) {
          setSubscribedPersist(true);
          return { ok: true, mode: 'webpush' };
        } else {
          throw new Error('Backend Subscribe fehlgeschlagen');
        }
      } catch(e) {
        console.warn('Web Push subscription failed:', e.message);
        // Fallback: nur push_enabled setzen (Polling funktioniert noch)
        await api.put('/notifications/settings', { push_enabled: true });
        setSubscribedPersist(true);
        return { ok: true, mode: 'polling_fallback', error: e.message };
      }
    } else {
      // pushManager nicht verfügbar — Polling-only
      await api.put('/notifications/settings', { push_enabled: true });
      setSubscribedPersist(true);
      return { ok: true, mode: 'polling' };
    }
  }, [setSubscribedPersist]);

  // Push deaktivieren
  const disable = useCallback(async () => {
    try {
      // Web Push Subscription im Browser löschen
      const reg = regRef.current || await navigator.serviceWorker?.ready.catch(() => null);
      if (reg?.pushManager) {
        const sub = await reg.pushManager.getSubscription().catch(() => null);
        if (sub) await sub.unsubscribe().catch(() => {});
      }
    } catch(e) {
      console.warn('Unsubscribe error:', e);
    }

    // Backend informieren
    try {
      await api.delete('/notifications/subscribe');
    } catch {
      try { await api.put('/notifications/settings', { push_enabled: false }); } catch {}
    }

    setSubscribedPersist(false);
  }, [setSubscribedPersist]);

  // Push aktiv = Berechtigung granted UND subscribed (aus localStorage/Backend)
  const pushActive = permission === 'granted' && subscribed;

  return {
    permission,
    subscribed,
    pushActive,
    installed,
    enable,
    disable,
  };
}
